import {
  Component,
  Element,
  Event,
  EventEmitter,
  Fragment,
  Host,
  JSX,
  Listen,
  Method,
  Prop,
  State,
  Watch,
  h,
} from '@stencil/core'

import { SelectOption, SelectOptionValue } from '../air-select-option/air-select-option.types'
import { KeyCode } from '../../enums/keycode.enum'
import { Validation } from '../../types/status.types'

function isSelectOption(instance: unknown): instance is SelectOption {
  return typeof instance === 'object' && Object.prototype.hasOwnProperty.call(instance, 'value')
}

@Component({
  tag: 'air-select',
  styleUrl: 'air-select.scss',
  shadow: true,
})
export class AirSelect {
  /** Reference to the host element */
  @Element() host!: HTMLAirSelectElement

  /** Specifies select label */
  @Prop() label?: string

  /** Defines the open state */
  @Prop({ mutable: true, reflect: true }) opened = false

  /** Specifies select value */
  @Prop({ mutable: true }) value?: SelectOptionValue
  @Watch('value')
  watchValue(newValue: SelectOptionValue, oldValue: SelectOptionValue): void {
    if (newValue !== oldValue) {
      const option = this.findOptionByValue(newValue)
      this.handleSelect(option)
    }
  }

  /** Specifies selected option index */
  @Prop({ mutable: true }) selectedIndex?: number
  @Watch('selectedIndex')
  watchSelectedIndex(newValue: number, oldValue: number): void {
    if (newValue !== oldValue) {
      const option = this.findOptionByIndex(newValue)
      this.handleSelect(option)
    }
  }

  /** Specifies placeholder value */
  @Prop() placeholder?: string = 'Select'

  /** Defines select options */
  @Prop({ mutable: true }) options: (SelectOption | string)[] = []
  @Watch('options')
  watchOptionsChange(): void {
    this.filteredOptions = [...this.options]
  }

  /** Defines if select should have autofocus */
  // eslint-disable-next-line @stencil-community/reserved-member-names
  @Prop() autofocus = false

  /** Specifies if select is read only */
  @Prop() readonly = false

  /** Specifies if select is required */
  @Prop() required = false

  /** Specifies if select is disabled */
  @Prop() disabled = false

  /** Specifies validation of select */
  @Prop() validation?: Validation

  /** Specifies icon indicator */
  @Prop() iconIndicator = 'chevron-down'

  /** Specifies if input filter is activated */
  @Prop() filter = false

  /** Specifies select options footer button label */
  @Prop() footerButton: string | undefined

  /** Specifies no results message */
  @Prop() noResultsMessage = 'No results, please try different keyword'

  /** Specifies select options footer button show delay in milliseconds */
  @Prop() footerButtonDelay = 0

  @State() filteredOptions: (SelectOption | string)[] = []

  @State() showFooterButton = false

  private triggerElement?: HTMLDivElement
  private selectedName?: string
  private filterInputElement: HTMLInputElement | undefined

  /** Triggers when select is open */
  @Event({ eventName: 'air-open' }) open!: EventEmitter<void>

  /** Triggers when select is closed */
  @Event({ eventName: 'air-close' }) close!: EventEmitter<void>

  /** Triggers when select is focused */
  @Event({ eventName: 'air-focus' }) focus!: EventEmitter<SelectOptionValue>

  /** Triggers when select is blur */
  @Event({ eventName: 'air-blur' }) blur!: EventEmitter<SelectOptionValue>

  /** Triggers when an input has lost focus */
  @Event({ eventName: 'air-change' }) change!: EventEmitter<SelectOptionValue>

  /** Triggers when an input has lost focus */
  @Event({ eventName: 'air-footer-button' }) footerButtonClick!: EventEmitter<void>

  @Listen('air-change-option')
  listenAirSelect(event: CustomEvent<SelectOption>): void {
    this.opened = false
    const value = this.getOptionValue(event.detail)
    this.change.emit(value)
    this.value = value
  }

  @Listen('air-open')
  listenOpen(): void {
    this.opened = true
  }

  @Listen('air-close')
  listenClose(): void {
    this.opened = false
  }

  @Listen('keydown', { passive: true })
  listenKeyDown(event: KeyboardEvent): void {
    if (this.disabled || this.readonly) return

    if (KeyCode.Escape === event.code) {
      this.opened = false
    }

    if (KeyCode.Space === event.code) {
      this.opened = !this.opened
    }

    const openKeyCodes: string[] = [KeyCode.Up, KeyCode.Down]
    if (openKeyCodes.includes(event.code)) {
      this.opened = true
    }
  }

  /** Manually sets focus to the trigger element */
  @Method()
  async setFocus(): Promise<void> {
    this.triggerElement && this.triggerElement.focus && this.triggerElement.focus()
  }

  /** Manually blur trigger element */
  @Method()
  async setBlur(): Promise<void> {
    this.triggerElement && this.triggerElement.blur && this.triggerElement.blur()
  }

  componentWillLoad(): void {
    if (this.options.length === 0) {
      const selectOptionsElements = this.getOptions()
      this.options = selectOptionsElements.map((optionElement: HTMLAirSelectOptionElement) => {
        return {
          value: optionElement.value,
          label:
            optionElement.label || optionElement.textContent || optionElement.value?.toString(),
          selected: optionElement.selected,
          disabled: optionElement.disabled,
        }
      })
    }

    const selectedOption = this.options.find((option, index) => {
      if (
        (isSelectOption(option) && (option.selected || option.value === this.value)) ||
        option === this.value
      ) {
        return true
      }

      return this.selectedIndex === index
    })

    this.handleSelect(selectedOption)
  }

  async componentDidLoad(): Promise<void> {
    this.autofocus && (await this.setFocus())
  }

  private getOptions(): HTMLAirSelectOptionElement[] {
    const rootSelectOptions = this.host.querySelectorAll('air-select-option')
    const selectOptions =
      rootSelectOptions.length > 0
        ? rootSelectOptions
        : this.host.shadowRoot?.querySelectorAll('air-select-option')
    return selectOptions ? Array.from(selectOptions) : []
  }

  private getOptionDisplayName(option: SelectOption | string | undefined): string | undefined {
    return isSelectOption(option) ? option.label || option.value?.toString() : option
  }

  private getOptionValue(option: SelectOption | string | undefined): SelectOptionValue | undefined {
    return isSelectOption(option) ? option.value : option
  }

  private updateOptionSelection(selectOption: SelectOption | string | undefined): void {
    const options = this.getOptions()
    options.forEach((option, index) => {
      const value = this.getOptionValue(selectOption)
      option.selected = false
      if (option.value === value) {
        option.selected = true
        this.selectedIndex = index
      }
    })
  }

  private findOptionByValue(optionValue: SelectOptionValue): SelectOption | string | undefined {
    return this.options.find(
      (option) =>
        (isSelectOption(option) && (option.selected || option.value === optionValue)) ||
        option === optionValue,
    )
  }

  private findOptionByIndex(index: number): SelectOption | string | undefined {
    return this.options.find((_, optionIndex) => optionIndex === index)
  }

  private handleFocus = (): void => {
    if (this.disabled) return
    this.focus.emit(this.value)
  }

  private handleBlur = (): void => {
    this.blur.emit(this.value)
  }

  private handleFilter = (): void => {
    this.opened = true
    const searchedString = this.filterInputElement?.value || ''

    this.filteredOptions = this.options.filter((option: string | SelectOption) => {
      if (isSelectOption(option)) {
        const selectOption = option.label || option.value

        const isMatch = this.isMatch(selectOption.toString(), searchedString)
        if (isMatch) return true
        if (option.index !== undefined) {
          const optionIndex = option.index.join(' ')
          return this.isMatch(optionIndex, searchedString)
        }
      }
      return this.isMatch(option.toString(), searchedString)
    })

    if (this.filteredOptions.length === 0) {
      this.buttonShowDelayTimer()
    }
  }

  private isMatch(selectOption: string, searchValue: string): boolean {
    return selectOption.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())
  }

  private buttonShowDelayTimer(): void {
    if (this.showFooterButton) return
    setTimeout(() => {
      this.showFooterButton = true
    }, this.footerButtonDelay)
  }

  private handleFooterButtonClick = (): void => {
    this.opened = false
    this.footerButtonClick.emit()
  }

  private handleSelect(selectOption: SelectOption | string | undefined): void {
    this.updateOptionSelection(selectOption)
    this.selectedName = this.getOptionDisplayName(selectOption)
    this.filteredOptions = [...this.options]
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (!this.opened || this.disabled || event.target !== this.host) return

    const nextKeys: string[] = [KeyCode.Right, KeyCode.Down]
    const prevKeys: string[] = [KeyCode.Left, KeyCode.Up]
    if ([...nextKeys, ...prevKeys].includes(event.code)) {
      event.preventDefault()
      const children = this.getOptions()
      if (children.length === 0) return

      const index = children.findIndex((child) => child === this.host.shadowRoot?.activeElement)

      if (index !== -1) {
        const nextIndex = index + (prevKeys.includes(event.code) ? -1 : 1)
        if (nextIndex >= 0 && nextIndex < children.length) {
          children[nextIndex].focus()
        }
      }
    }
  }

  private renderSelectedOption(): JSX.Element {
    const attributes = {
      class: 'selected-name',
      placeholder: this.placeholder,
      value: this.selectedName || '',
      readonly: !this.filter,
      tabindex: this.filter ? '0' : '-1',
      onInput: this.handleFilter,
      ref: (el: HTMLInputElement | undefined): void => {
        this.filterInputElement = el
      },
    }
    return <input {...attributes} />
  }

  private renderOptions(): JSX.Element {
    return this.filteredOptions.map((option: SelectOption | string) => {
      if (isSelectOption(option)) {
        const { selected, disabled, value } = option
        const attributes = {
          selected,
          disabled,
          value,
        }
        return (
          <air-select-option {...attributes}>
            {option.label || option.value?.toString()}
          </air-select-option>
        )
      } else {
        return <air-select-option value={option}>{option}</air-select-option>
      }
    })
  }

  private renderLabel(): HTMLLabelElement {
    return (
      this.label !== undefined && (
        <label class="select-label" htmlFor="select">
          {this.label}
          {this.required && <span class="required-mark">*</span>}
        </label>
      )
    )
  }

  private renderValidation(): JSX.Element {
    return (
      this.validation?.text !== undefined && <span class="status-text">{this.validation.text}</span>
    )
  }

  render(): JSX.Element {
    const opened = this.opened && !this.disabled && !this.readonly

    const hostAttributes = {
      role: 'listbox',
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onKeyDown: this.handleKeyDown,
    }

    const selectWrapperClasses = {
      'select-wrapper': true,
      opened: opened,
      [`with-status`]: this.validation?.status !== undefined,
      [`${this.validation?.status}`]: this.validation?.status !== undefined,
    }

    const dropdownTriggerClass = {
      'trigger-wrapper': true,
      'select-element': true,
      disabled: this.disabled,
      readonly: this.readonly,
    }

    const dropdownTriggerAttributes = {
      class: dropdownTriggerClass,
      ref: (element?: HTMLDivElement): void => {
        this.triggerElement = element
      },
      tabindex: this.disabled ? -1 : 0,
    }

    const dropdownBaseAttributes = {
      disabled: this.disabled || this.readonly,
      opened,
    }

    return (
      <Host {...hostAttributes}>
        <div class={selectWrapperClasses}>
          {this.renderLabel()}
          <air-dropdown-base {...dropdownBaseAttributes}>
            <div slot="trigger" class="trigger">
              <div {...dropdownTriggerAttributes}>
                {this.renderSelectedOption()}
                <air-icon class="indicator" name={this.iconIndicator}></air-icon>
              </div>
            </div>
            {this.filteredOptions.length > 0 ? (
              this.renderOptions()
            ) : (
              <Fragment>
                <p class="no-results">{this.noResultsMessage}</p>
                {this.footerButton && this.showFooterButton && (
                  <button
                    class="footer-button"
                    type="button"
                    onClick={this.handleFooterButtonClick}
                  >
                    {this.footerButton}
                  </button>
                )}
              </Fragment>
            )}
          </air-dropdown-base>
          {this.renderValidation()}
        </div>
      </Host>
    )
  }
}
